import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Pagination,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Budget } from '../../types';
import { budgets } from '../../services/api';
import BudgetCard from './BudgetCard';
import NewBudgetDialog from './NewBudgetDialog';

const BUDGETS_PER_PAGE = 2;

const BudgetList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [budgetList, setBudgetList] = useState<Budget[]>([]);
  const [isNewBudgetDialogOpen, setIsNewBudgetDialogOpen] = useState(false);
  const [page, setPage] = useState(1);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const data = await budgets.getAll();
      // Sort budgets by date, newest first
      const sortedBudgets = data.sort((a: Budget, b: Budget) => new Date(b.month).getTime() - new Date(a.month).getTime());
      setBudgetList(sortedBudgets);
      setError(null);
    } catch (err) {
      setError('Failed to load budgets. Please try again later.');
      console.error('Error fetching budgets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  const handleCreateBudget = async (newBudget: Omit<Budget, '_id' | 'user' | 'createdAt'>) => {
    try {
      await budgets.create(newBudget);
      setIsNewBudgetDialogOpen(false);
      fetchBudgets();
    } catch (err) {
      setError('Failed to create budget. Please try again.');
      console.error('Error creating budget:', err);
    }
  };

  const handleBudgetUpdate = async (budgetId: string, updatedFields: Partial<Budget>) => {
    // Update the local state optimistically to avoid unnecessary API calls
    setBudgetList(prevBudgets => 
      prevBudgets.map(budget => 
        budget._id === budgetId 
          ? { ...budget, ...updatedFields }
          : budget
      )
    );
  };

  const handleBudgetDelete = async (budgetId: string) => {
    // Remove the deleted budget from local state
    setBudgetList(prevBudgets => 
      prevBudgets.filter(budget => budget._id !== budgetId)
    );
    
    // Refresh the list to ensure we have the latest data from server
    // This is necessary because deleting a budget also deletes related transactions
    setTimeout(() => {
      fetchBudgets();
    }, 500); // Small delay to allow server to process the deletion
  };

  // Calculate pagination values
  const totalPages = Math.ceil(budgetList.length / BUDGETS_PER_PAGE);
  const startIndex = (page - 1) * BUDGETS_PER_PAGE;
  const paginatedBudgets = budgetList.slice(startIndex, startIndex + BUDGETS_PER_PAGE);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Container 
        maxWidth={isMobile ? "sm" : "xl"} 
        sx={{ 
          py: { xs: 1, sm: 2 }, 
          px: { xs: 1, sm: 2, md: 3 },
          position: 'relative' 
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={isMobile ? "sm" : "xl"} 
      sx={{ 
        py: { xs: 1, sm: 2 }, 
        px: { xs: 1, sm: 2, md: 3 },
        position: 'relative' 
      }}
    >
      <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Budgets
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setIsNewBudgetDialogOpen(true)}
          sx={{
            backgroundColor: '#7C8B6F',
            '&:hover': {
              backgroundColor: '#6A7860'
            }
          }}
        >
          New Budget
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {paginatedBudgets.map((budget) => (
          <Grid item xs={12} sm={6} md={6} key={budget._id}>
            <BudgetCard 
              budget={budget} 
              onUpdate={(updatedFields) => handleBudgetUpdate(budget._id, updatedFields)}
              onDelete={() => handleBudgetDelete(budget._id)}
            />
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <NewBudgetDialog
        open={isNewBudgetDialogOpen}
        onClose={() => setIsNewBudgetDialogOpen(false)}
        onSubmit={handleCreateBudget}
      />
      </Box>
    </Container>
  );
};

export default BudgetList; 